import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { formatPropertyAddress } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  Plus,
  Trash2,
  Loader2,
  Download,
  AlertTriangle,
  GraduationCap,
  CheckCircle2,
  Info,
  Send,
  Users,
  RefreshCw,
  PenLine,
  Building2,
  History,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddressInput } from '../components/AddressInput';
import { StepIndicator } from '../components/written-statement/StepIndicator';
import { ClauseLibrary } from '../components/written-statement/ClauseLibrary';
import { normalizePersonFullName, validatePersonFullName } from '../lib/personNames';
import { PdfPreviewDialog } from '../components/PdfPreviewDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPLIANCE_ITEMS = [
  {
    title: 'Deposit protection',
    description:
      'If you have taken a deposit, protect it in a government-approved scheme (DPS, mydeposits or TDS) within 30 days of receipt and serve the prescribed information on your tenant within the same period.',
  },
  {
    title: 'Gas Safety Certificate (CP12)',
    description:
      'Provide your tenant with a copy of a valid Gas Safety Certificate before the tenancy starts. Renew annually and provide a new copy within 28 days of each renewal check.',
  },
  {
    title: 'Electrical Installation Condition Report (EICR)',
    description:
      'Ensure you have a valid EICR issued within the past 5 years. Provide a copy to your tenant at or before the tenancy start. Remedy any Category 1 or 2 defects within 28 days.',
  },
  {
    title: 'Energy Performance Certificate (EPC)',
    description:
      "Provide your tenant with a copy of a valid EPC. The property must have a minimum E rating (unless a valid exemption applies). EPC must be in place before marketing and at tenancy start.",
  },
  {
    title: 'Smoke and carbon monoxide alarms',
    description:
      'Ensure working smoke alarms are installed on every storey used as living accommodation, and carbon monoxide alarms in any room with a fixed combustion appliance. Test all alarms on the first day of the tenancy.',
  },
  {
    title: 'Right to Rent checks',
    description:
      'Complete Right to Rent checks for all adult occupants aged 18 and over before the tenancy starts. Retain copies of all documents inspected. Failure to comply can result in a civil penalty.',
  },
  {
    title: 'Register on the PRS Database',
    description:
      "Register as a private landlord on the Private Rented Sector (PRS) Database as required under the Renters' Rights Act 2025. Registration must be in place before the tenancy commences.",
  },
  {
    title: "Renters' Rights Act Information Sheet",
    description:
      "Serve the Renters' Rights Act 2025 Information Sheet on your tenant. This is a statutory requirement for all new tenancies from the commencement date of the Act. A link is not sufficient — the document must be provided.",
  },
];

const UTILITY_KEYS = ['gas', 'electricity', 'water', 'internet', 'council_tax', 'tv_licence'];
const UTILITY_LABELS = {
  gas: 'Gas',
  electricity: 'Electricity',
  water: 'Water',
  internet: 'Internet',
  council_tax: 'Council Tax',
  tv_licence: 'TV Licence',
};
const UTILITY_OPTIONS = [
  { value: 'tenant_pays', label: 'Tenant pays' },
  { value: 'included', label: 'Included in rent' },
  { value: 'not_applicable', label: 'Not applicable' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const emptyAddress = () => ({ line1: '', line2: '', city: '', postcode: '', country: 'United Kingdom' });

const formatStructuredAddress = (address, fallback = '') => {
  if (!address || typeof address !== 'object') return fallback || '';
  const parts = [address.line1, address.line2, address.city, address.postcode, address.country]
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(', ') : fallback || '';
};

// Format a structured address to match AddressInput's helperText hint:
// "Flat 4, 12 High Street, London SW1A 1AA, UK"
// city and postcode are space-joined; country is always shown as "UK".
const fmtAddressForInput = (address) => {
  if (!address || typeof address !== 'object') return '';
  const line1 = String(address.line1 || '').trim();
  const line2 = String(address.line2 || '').trim();
  const city = String(address.city || '').trim();
  const postcode = String(address.postcode || '').trim();
  const country = address.country ? 'UK' : '';
  const cityPostcode = [city, postcode].filter(Boolean).join(' ');
  return [line1, line2, cityPostcode, country].filter(Boolean).join(', ');
};

const parseAddressString = (str) => {
  if (!str || typeof str !== 'string') return emptyAddress();
  const raw = str.trim();
  // Extract country suffix if present — keep it in the structured object.
  let country = 'United Kingdom';
  let s = raw;
  const countryMatch = raw.match(
    /,\s*(United Kingdom|UK|England|Scotland|Wales|Northern Ireland)\s*$/i
  );
  if (countryMatch) {
    // Normalise short-form "UK" to full name to match what Google Places returns.
    const matched = countryMatch[1];
    country = matched.toLowerCase() === 'uk' ? 'United Kingdom' : matched;
    s = raw.slice(0, raw.length - countryMatch[0].length).trim();
  }
  const pcMatch = s.match(/([A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2})\s*$/i);
  const postcode = pcMatch ? pcMatch[1].trim().toUpperCase() : '';
  const withoutPc = postcode ? s.slice(0, -postcode.length).replace(/,\s*$/, '').trim() : s;
  const parts = withoutPc.split(',').map((p) => p.trim()).filter(Boolean);
  return {
    line1: parts[0] || '',
    line2: parts.length > 2 ? parts.slice(1, -1).join(', ') : '',
    city: parts.length > 1 ? parts[parts.length - 1] : '',
    postcode,
    country,
  };
};

const parsePropertyAddress = (prop) => {
  if (!prop) return emptyAddress();
  const address = prop.address || '';
  const postcode = prop.postcode || '';

  // If address includes postcode at the end, remove it for parsing
  let addressWithoutPostcode = address;
  if (postcode && address.toUpperCase().endsWith(postcode.toUpperCase())) {
    addressWithoutPostcode = address.slice(0, -postcode.length).trim();
    if (addressWithoutPostcode.endsWith(',')) {
      addressWithoutPostcode = addressWithoutPostcode.slice(0, -1).trim();
    }
  }

  const parts = addressWithoutPostcode.split(',').map((p) => p.trim()).filter(Boolean);

  return {
    line1: parts.length > 0 ? parts[0] : addressWithoutPostcode,
    line2: parts.length > 1 ? parts.slice(1, -1).join(', ') : '',
    city: parts.length > 1 ? parts[parts.length - 1] : '',
    postcode: postcode,
    country: prop.country || 'United Kingdom',
  };
};

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '' : `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
};

const fmtStatementDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTenancyTenantNames = (tenancy) =>
  Array.isArray(tenancy?.tenants)
    ? tenancy.tenants.map((tenant) => tenant?.name).filter(Boolean)
    : [];

const getTenancyTenantEmails = (tenancy) =>
  Array.isArray(tenancy?.tenants)
    ? tenancy.tenants.map((tenant) => tenant?.email).filter(Boolean)
    : [];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WrittenStatement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    document.title = 'Written Statement — SIYAK';
  }, []);

  // Wizard
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Data
  const [properties, setProperties] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [tenanciesLoading, setTenanciesLoading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedTenancyId, setSelectedTenancyId] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedTenancy, setSelectedTenancy] = useState(null);
  const [clauseLibrary, setClauseLibrary] = useState([]);
  const [autoIncluded, setAutoIncluded] = useState([]);
  const [statementId, setStatementId] = useState(null);
  const [statementStatus, setStatementStatus] = useState('draft');
  const hydratingTenancyRef = useRef(false);
  // Set to true the moment loadDraftIntoState begins; never reset within the same
  // component lifetime. Prevents the tenancy useEffect from firing destructively
  // when the property effect asynchronously reloads tenancies after initial hydration.
  const statementLoadedRef = useRef(false);
  const draftAutosaveTimerRef = useRef(null);
  const hasHydratedDraftRef = useRef(false);
  // Synchronous guard that prevents a second POST before React has flushed
  // the setSaving(true) state update (covers rapid double-clicks and stale closures).
  const savingRef = useRef(false);
  // Snapshot of the form body at the moment the last PDF was generated.
  // Used to block Regenerate PDF when nothing has changed.
  const lastGeneratedSnapshotRef = useRef(null);

  // Step 1 — Parties
  const [isStudentLet, setIsStudentLet] = useState(false);
  const [tenancyType, setTenancyType] = useState('whole_property');
  const [designatedRoomName, setDesignatedRoomName] = useState('');
  const [designatedRoomDescription, setDesignatedRoomDescription] = useState('');
  const [landlordNames, setLandlordNames] = useState([user?.name || '']);
  const [landlordAddress, setLandlordAddress] = useState(
    user?.business_address ? parseAddressString(user.business_address) : { line1: '', line2: '', city: '', postcode: '' }
  );
  const [landlordEmail, setLandlordEmail] = useState(user?.email || '');
  const [tenants, setTenants] = useState([{ name: '', email: '' }]);
  const [tenancyStartDate, setTenancyStartDate] = useState('');
  const [maxOccupants, setMaxOccupants] = useState('');

  // Step 2 — Rent & Deposit
  const [rentAmount, setRentAmount] = useState('');
  const [rentPeriod, setRentPeriod] = useState('monthly');
  const [rentDueDay, setRentDueDay] = useState('1');
  const [initialRentPayment, setInitialRentPayment] = useState('');
  const [paymentMethodType, setPaymentMethodType] = useState('');
  const [bankDetails, setBankDetails] = useState({
    sortCode: '',
    accountNumber: '',
    accountName: '',
    accountType: 'personal'
  });
  const [depositTaken, setDepositTaken] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositScheme, setDepositScheme] = useState('');
  const [depositHeldBy, setDepositHeldBy] = useState('');
  const [utilities, setUtilities] = useState({
    gas: 'tenant_pays', electricity: 'tenant_pays', water: 'tenant_pays',
    internet: 'tenant_pays', council_tax: 'tenant_pays', tv_licence: 'tenant_pays',
  });

  // Step 3 — Additional Terms
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [clauseOverrides, setClauseOverrides] = useState({});

  // Step 4 — Custom Clauses + Guarantors
  const [customClauses, setCustomClauses] = useState([]);
  const [guarantors, setGuarantors] = useState([]);  // captured in Step 4, sent pre-generation

  // Step 5 — Review & Generate
  const [pdfGenerated, setPdfGenerated] = useState(false);
  const [pdfPath, setPdfPath] = useState(null);
  const [titleType, setTitleType] = useState(null);
  const [viewingDraft, setViewingDraft] = useState(false);
  const [draftPreview, setDraftPreview] = useState(null);
  const [versionPreview, setVersionPreview] = useState(null);
  const [draftPreviewLoading, setDraftPreviewLoading] = useState(false);
  const draftPreviewRequestRef = useRef(0);
  const draftPreviewAbortRef = useRef(null);
  const [statementVersions, setStatementVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Signing panel state
  const [signingVersion, setSigningVersion] = useState(null);   // latest version doc
  const [signingLoading, setSigningLoading] = useState(false);
  const [sendingForSigning, setSendingForSigning] = useState(false);
  const [amending, setAmending] = useState(false);
  const [resending, setResending] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);

  const isEditableStatement = !searchParams.get('view') && (!statementId || statementStatus === 'draft');
  const isViewOnly = Boolean(searchParams.get('view'));
  // True when the landlord can take PDF/signing actions (draft or freshly generated, not view-only).
  // Intentionally separate from isEditableStatement: generation is the contract boundary —
  // form fields are locked after generation but actions (download, send, amend) remain available.
  const canActOnStatement = !isViewOnly && ['draft', 'generated'].includes(statementStatus);

  const markStatementChanged = () => {
    try {
      window.sessionStorage.setItem('siyak_written_statement_updated_at', String(Date.now()));
    } catch {
      // Non-critical; dashboard re-fetches from backend on mount/tab activation.
    }
  };

  // -------------------------------------------------------------------------
  // Initial data fetch
  // -------------------------------------------------------------------------

  const fetchClauseLibrary = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.set(k, v);
      });
      const { data } = await axios.get(`${API_URL}/api/clause-library?${params}`);
      setClauseLibrary(data);
      const auto = data.filter((c) => c.auto_included).map((c) => c.id);
      setAutoIncluded(auto);
      const availableClauseIds = new Set(data.map((c) => c.id));
      const defaults = data
        .filter((c) => c.default_on || c.auto_included)
        .map((c) => c.id);
      setSelectedClauses((prev) => {
        const retained = prev.filter((id) => availableClauseIds.has(id));
        const merged = [...new Set([...retained, ...defaults])];
        return merged;
      });
      setClauseOverrides((prev) => Object.fromEntries(
        Object.entries(prev).filter(([clauseId]) => availableClauseIds.has(clauseId))
      ));
    } catch {
      // clause library is optional; don't block on failure
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [propsRes] = await Promise.all([
          axios.get(`${API_URL}/api/properties`, { withCredentials: true }),
          fetchClauseLibrary(),
        ]);
        const propList = propsRes.data || [];
        setProperties(propList);

        const qPropId = searchParams.get('propertyId');
        const qTenancyId = searchParams.get('tenancyId');
        const qId = searchParams.get('id');

        if (qId) {
          // Explicit id: load the statement, then validate that tenancyId (if also
          // present) agrees. If they disagree, correct the URL silently so the loaded
          // statement is always the authoritative record.
          const { data } = await axios.get(
            `${API_URL}/api/written-statements/${qId}`,
            { withCredentials: true }
          );
          if (qTenancyId && data.tenancy_id && data.tenancy_id !== qTenancyId) {
            toast.warning('URL parameters were inconsistent — loaded the correct record.');
            const corrected = new URLSearchParams(searchParams);
            corrected.set('tenancyId', data.tenancy_id);
            navigate(`/tenancy-agreement?${corrected.toString()}`, { replace: true });
          }

          const qAmend = searchParams.get('amend') === '1';
          if (qAmend && data.status !== 'draft') {
            // Call the amend API before loading so the form opens editable
            try {
              await axios.post(
                `${API_URL}/api/written-statements/${data.id}/amend`,
                {},
                { withCredentials: true }
              );
              toast.success('A pre-filled draft has been created — make your changes and regenerate the PDF.');
            } catch (err) {
              toast.error(fmtApiError(err, 'Failed to create amendment'));
            }
            // Re-fetch then load the fresh draft
            const { data: amended } = await axios.get(
              `${API_URL}/api/written-statements/${data.id}`,
              { withCredentials: true }
            );
            await loadDraftIntoState(amended, propList);
            // Strip ?amend from URL so a hard-refresh doesn't re-amend
            const cleanParams = new URLSearchParams(searchParams);
            cleanParams.delete('amend');
            navigate(`/tenancy-agreement?${cleanParams.toString()}`, { replace: true });
          } else {
            await loadDraftIntoState(data, propList);
          }
        } else if (qTenancyId) {
          // tenancyId present but no id: proactively resolve the canonical statement
          // for this tenancy instead of opening a blank wizard and forcing a 409 round-trip.
          try {
            const { data: stmts } = await axios.get(
              `${API_URL}/api/written-statements`,
              { withCredentials: true }
            );
            const canonical = Array.isArray(stmts)
              ? stmts.find((s) => s.tenancy_id === qTenancyId)
              : null;
            if (canonical) {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('id', canonical.id);
              navigate(`/tenancy-agreement?${newParams.toString()}`, { replace: true });
              const { data: full } = await axios.get(
                `${API_URL}/api/written-statements/${canonical.id}`,
                { withCredentials: true }
              );
              await loadDraftIntoState(full, propList);
              return;
            }
          } catch {
            // Non-fatal — fall through to blank wizard for a genuinely new statement.
          }
          // No existing statement: pre-select property/tenancy for a fresh wizard.
          if (qPropId) setSelectedPropertyId(qPropId);
          setSelectedTenancyId(qTenancyId);
        } else {
          if (qPropId) setSelectedPropertyId(qPropId);
        }
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When selectedPropertyId changes, load property context and available tenancies
  useEffect(() => {
    if (!selectedPropertyId || !properties.length) {
      setSelectedProperty(null);
      setTenancies([]);
      setSelectedTenancy(null);
      setSelectedTenancyId('');
      return;
    }
    const prop = properties.find((p) => p.id === selectedPropertyId);
    if (!prop) return;
    setSelectedProperty(prop);
    let cancelled = false;

    const loadTenancies = async () => {
      try {
        setTenanciesLoading(true);
        const { data } = await axios.get(
          `${API_URL}/api/tenancies/property/${selectedPropertyId}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const nextTenancies = Array.isArray(data?.tenancies) ? data.tenancies : [];
        setTenancies(nextTenancies);
        setSelectedTenancyId((current) => (
          nextTenancies.some((tenancy) => tenancy.id === current) ? current : ''
        ));
      } catch {
        if (!cancelled) {
          setTenancies([]);
          setSelectedTenancyId('');
        }
      } finally {
        if (!cancelled) setTenanciesLoading(false);
      }
    };

    loadTenancies();

    return () => {
      cancelled = true;
    };
  }, [selectedPropertyId, properties]);

  useEffect(() => {
    if (!selectedTenancyId || !tenancies.length) {
      setSelectedTenancy(null);
      return;
    }
    const tenancy = tenancies.find((item) => item.id === selectedTenancyId) || null;
    setSelectedTenancy(tenancy);
    if (!tenancy) return;

    // Suppress if we are hydrating an existing statement — the initial ref guard handles
    // the first fire; statementLoadedRef handles subsequent fires (e.g. when the property
    // effect reloads tenancies async, triggering this effect a second time).
    if (hydratingTenancyRef.current) {
      hydratingTenancyRef.current = false;
      return;
    }
    if (statementLoadedRef.current) return;

    setPdfGenerated(false);
    setPdfPath(null);
    setTitleType(null);
    if (selectedProperty?.rent) setRentAmount(String(selectedProperty.rent));
    const prefetchedTenants = tenancy.tenants.map((tenant) => ({
      name: tenant?.name || '',
      email: tenant?.email || '',
      user_id: tenant?.user_id || '',
    }));
    setTenants(prefetchedTenants);
    setTenancyStartDate(tenancy.start_date ? tenancy.start_date.slice(0, 10) : '');
    const hasDeposit = Number(tenancy.deposit_amount || 0) > 0;
    setDepositTaken(hasDeposit);
    setDepositAmount(hasDeposit && tenancy.deposit_amount != null ? String(tenancy.deposit_amount) : '');
    setDepositScheme('');
    setDepositHeldBy('');
  }, [selectedTenancyId, tenancies, selectedProperty]);

  // Refresh clause library when tenancy/property filters change
  useEffect(() => {
    if (!selectedProperty) return;
    // Refresh clause library with property-specific filters
    fetchClauseLibrary({
      has_garden: selectedProperty.features?.includes('garden') || false,
      is_furnished: selectedProperty.furnishing === 'furnished' || selectedProperty.furnishing === 'part_furnished',
      is_student_let: isStudentLet,
      deposit_taken: depositTaken,
      multiple_tenants: tenants.length > 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, isStudentLet, depositTaken, tenants.length]);

  // -------------------------------------------------------------------------
  // Load existing draft
  // -------------------------------------------------------------------------

  const loadDraftIntoState = async (data, propList) => {
    statementLoadedRef.current = true;  // suppress tenancy effect for this statement's lifetime
    setStatementId(data.id);
    setStatementStatus(data.status || 'draft');
    setSelectedPropertyId(data.property_id);
    hydratingTenancyRef.current = true;
    setSelectedTenancyId(data.tenancy_id || '');
    const prop = propList.find((p) => p.id === data.property_id);
    if (prop) setSelectedProperty(prop);
    setIsStudentLet(data.is_student_let || false);
    setTenancyType(data.tenancy_type || 'whole_property');

    const ws = data.written_statement || {};
    const ll = ws.landlord || {};
    setLandlordNames(ll.names?.length ? ll.names : [user?.name || '']);
    setLandlordAddress(ll.notice_address || { line1: '', line2: '', city: '', postcode: '' });
    setLandlordEmail(ll.email || user?.email || '');
    setTenants(ws.tenants?.length ? ws.tenants : [{ name: '', email: '' }]);
    setTenancyStartDate(ws.tenancy_start_date ? ws.tenancy_start_date.slice(0, 10) : '');
    setMaxOccupants(ws.max_occupants ? String(ws.max_occupants) : '');
    setDesignatedRoomName(ws.designated_room_name || '');
    setDesignatedRoomDescription(ws.designated_room_description || '');

    const r = ws.rent || {};
    setRentAmount(r.amount ? String(r.amount) : '');
    setRentPeriod(r.period || 'monthly');
    setRentDueDay(r.due_day ? String(r.due_day) : '1');
    setInitialRentPayment(ws.initial_rent_payment ? String(ws.initial_rent_payment) : '');
    if (r.payment_method) {
      setPaymentMethodType(r.payment_method.type || '');
      setBankDetails(r.payment_method.details || {
        sortCode: '',
        accountNumber: '',
        accountName: '',
        accountType: 'personal'
      });
    } else {
      setPaymentMethodType('');
      setBankDetails({
        sortCode: '',
        accountNumber: '',
        accountName: '',
        accountType: 'personal'
      });
    }

    const d = ws.deposit || {};
    setDepositTaken(!!d.amount && d.amount > 0);
    setDepositAmount(d.amount ? String(d.amount) : '');
    setDepositScheme(d.scheme || '');
    setDepositHeldBy(d.held_by || '');

    if (ws.utilities) setUtilities(ws.utilities);

    const opt = data.optional_terms || {};
    setSelectedClauses(opt.selected_clauses || []);
    setClauseOverrides(opt.clause_overrides || {});
    setCustomClauses(opt.custom_clauses || []);
    setGuarantors(opt.guarantors || []);

    const documentState = data.document || {};
    const isDraft = !data.status || data.status === 'draft';
    const viewOnly = Boolean(new URLSearchParams(window.location.search).get('view'));

    if (isDraft && !viewOnly) {
      // Draft statements always open at Step 1 in editing mode.
      // Any previously-generated PDF is accessible via the version history card.
      setPdfGenerated(false);
      setPdfPath(null);
      setTitleType(documentState.title_type || null);
      await loadStatementVersions(data.id);
    } else {
      // Sent / signed / superseded, OR draft opened in view-only mode —
      // show the finalised view on Step 5 with all steps populated (read-only).
      // Trust has_versions as proof a PDF was generated even if the file path
      // was later nulled by reconcile (e.g. Docker volume mismatch in dev).
      const hasDownloadablePdf = Boolean(
        documentState.has_versions ||
        documentState.final_pdf_path ||
        documentState.pdf_path ||
        documentState.download_available
      );
      const resolvedPdfPath = documentState.final_pdf_path || documentState.pdf_path || null;
      setPdfGenerated(hasDownloadablePdf);
      setPdfPath(resolvedPdfPath);
      setTitleType(documentState.title_type || null);
      setCurrentStep(5);
      await loadSigningStatus(data.id);
      const versions = await loadStatementVersions(data.id);
      // If the main pdf_path was nulled (e.g. by reconcile after file went missing)
      // fall back to the most recent available version's path so download still works.
      if (!resolvedPdfPath && versions.length) {
        const fallback = versions.find((v) => v.pdf_path) || null;
        if (fallback?.pdf_path) setPdfPath(fallback.pdf_path);
      }
    }
  };

  // -------------------------------------------------------------------------
  // Build request body
  // -------------------------------------------------------------------------

  const buildRequestBody = () => {
    const propAddr = parsePropertyAddress(selectedProperty);
    const isRoom = tenancyType === 'room_in_shared_house';
    return {
      tenancy_id: selectedTenancyId,
      tenancy_type: tenancyType,
      is_student_let: isStudentLet,
      written_statement: {
        landlord: {
          names: landlordNames.filter((n) => n.trim()).map(normalizePersonFullName),
          notice_address: landlordAddress,
          email: landlordEmail,
        },
        tenants: tenants.filter((t) => t.name.trim()).map((tenant) => ({
          ...tenant,
          name: normalizePersonFullName(tenant.name),
          email: String(tenant.email || '').trim(),
        })),
        property_address: propAddr,
        tenancy_start_date: tenancyStartDate,
        rent: {
          amount: parseFloat(rentAmount) || 0,
          period: rentPeriod,
          due_day: parseInt(rentDueDay) || 1,
          payment_method: {
            type: paymentMethodType,
            details: paymentMethodType === 'bank_transfer' ? bankDetails : null,
          },
        },
        initial_rent_payment: initialRentPayment && parseFloat(initialRentPayment) > 0 ? parseFloat(initialRentPayment) : null,
        deposit: {
          amount: depositTaken && depositAmount ? parseFloat(depositAmount) : null,
          scheme: depositTaken && depositScheme ? depositScheme : null,
          held_by: depositTaken && depositHeldBy ? depositHeldBy : null,
        },
        utilities,
        max_occupants: maxOccupants ? parseInt(maxOccupants) : null,
        tenant_notice_period_months: 2,
        designated_room_name: isRoom && designatedRoomName.trim() ? designatedRoomName.trim() : null,
        designated_room_description: isRoom && designatedRoomDescription.trim() ? designatedRoomDescription.trim() : null,
      },
      optional_terms: {
        selected_clauses: selectedClauses,
        clause_overrides: clauseOverrides,
        custom_clauses: customClauses.filter((c) => c.title?.trim() && c.body?.trim()),
        guarantors: guarantors.filter((g) => g.name?.trim() && g.email?.trim()),
      },
    };
  };

  // -------------------------------------------------------------------------
  // Save draft
  // -------------------------------------------------------------------------

  const handleSaveDraft = async () => {
    if (!isEditableStatement) {
      toast.error('This statement is read-only. Create an amendment to make changes.');
      return null;
    }
    // Synchronous ref guard prevents a second request from being fired before
    // React has flushed the saving=true state update (covers rapid double-clicks).
    if (savingRef.current) return null;
    savingRef.current = true;
    setSaving(true);
    try {
      const body = buildRequestBody();
      let id = statementId;
      if (!id) {
        let data;
        try {
          ({ data } = await axios.post(
            `${API_URL}/api/written-statements/`,
            body,
            { withCredentials: true }
          ));
        } catch (postErr) {
          // 409 means a statement already exists for this tenancy — redirect to it
          // instead of surfacing a confusing error.
          if (postErr.response?.status === 409) {
            const body409 = postErr.response.data?.detail;
            const existingId = typeof body409 === 'object' ? body409.existing_id : null;
            if (existingId) {
              toast.info('A statement already exists for this tenancy — opening it now.');
              const params = new URLSearchParams(window.location.search);
              params.set('id', existingId);
              navigate(`/tenancy-agreement?${params.toString()}`, { replace: true });
              // Reload the existing statement into state
              const { data: existing } = await axios.get(
                `${API_URL}/api/written-statements/${existingId}`,
                { withCredentials: true }
              );
              // Hydrate the full form state so the loaded statement's fields are
              // visible immediately — setStatementId alone left the wizard blank.
              await loadDraftIntoState(existing, properties);
              return existingId;
            }
          }
          throw postErr;
        }
        id = data.id;
        setStatementId(id);
        setStatementStatus(data.status || 'draft');
        setStatementVersions([]);
      } else {
        // PUT is only reachable when isEditableStatement === true, which requires
        // statementStatus === 'draft'. Always reflect that in state: draft edits
        // invalidate the previously generated PDF and version record.
        await axios.put(
          `${API_URL}/api/written-statements/${id}`,
          body,
          { withCredentials: true }
        );
        setStatementStatus('draft');
        setPdfGenerated(false);
        setPdfPath(null);
        setTitleType(null);
        await loadStatementVersions(id);
      }
      markStatementChanged();
      toast.success('Draft saved');
      return id;
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to save draft'));
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const saveDraftQuietly = useCallback(async () => {
    if (!isEditableStatement || savingRef.current || !selectedPropertyId || !selectedTenancyId) return null;
    savingRef.current = true;
    try {
      const body = buildRequestBody();
      let id = statementId;
      if (!id) {
        const { data } = await axios.post(`${API_URL}/api/written-statements/`, body, { withCredentials: true });
        id = data.id;
        setStatementId(id);
        setStatementStatus(data.status || 'draft');
      } else {
        await axios.put(`${API_URL}/api/written-statements/${id}`, body, { withCredentials: true });
        setStatementStatus('draft');
      }
      markStatementChanged();
      return id;
    } catch {
      return null;
    } finally {
      savingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditableStatement, selectedPropertyId, selectedTenancyId, statementId, landlordNames, landlordAddress, landlordEmail, tenants, tenancyStartDate, rentAmount, rentPeriod, rentDueDay, initialRentPayment, paymentMethodType, bankDetails, depositTaken, depositAmount, depositScheme, depositHeldBy, utilities, selectedClauses, clauseOverrides, customClauses, guarantors, isStudentLet, maxOccupants, tenancyType, designatedRoomName, designatedRoomDescription]);

  useEffect(() => {
    if (!hasHydratedDraftRef.current) {
      hasHydratedDraftRef.current = true;
      return undefined;
    }
    if (!isEditableStatement || !selectedPropertyId || !selectedTenancyId) return undefined;
    window.clearTimeout(draftAutosaveTimerRef.current);
    draftAutosaveTimerRef.current = window.setTimeout(() => {
      saveDraftQuietly();
    }, 1000);
    return () => window.clearTimeout(draftAutosaveTimerRef.current);
  }, [isEditableStatement, selectedPropertyId, selectedTenancyId, landlordNames, landlordAddress, landlordEmail, tenants, tenancyStartDate, rentAmount, rentPeriod, rentDueDay, initialRentPayment, paymentMethodType, bankDetails, depositTaken, depositAmount, depositScheme, depositHeldBy, utilities, selectedClauses, clauseOverrides, customClauses, guarantors, isStudentLet, maxOccupants, tenancyType, designatedRoomName, designatedRoomDescription, saveDraftQuietly]);

  // Capture a baseline snapshot the first time versions appear (e.g. on page load
  // with an existing statement). This ensures Regenerate PDF is blocked unless
  // the form has actually changed, even across page reloads.
  useEffect(() => {
    if (statementVersions.length > 0 && lastGeneratedSnapshotRef.current === null) {
      lastGeneratedSnapshotRef.current = JSON.stringify(buildRequestBody());
    }
    // buildRequestBody reads React state via closure — intentionally omitted from
    // deps so we snapshot exactly once when versions first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementVersions]);

  // -------------------------------------------------------------------------
  // Error formatting helper
  // -------------------------------------------------------------------------

  // Pydantic v2 returns detail as an array of {type,loc,msg,input,ctx,url} objects.
  // Always convert to a plain string before passing to toast.
  const fmtApiError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join('; ');
    if (typeof detail === 'object') return detail.message || detail.msg || fallback;
    return fallback;
  };

  // -------------------------------------------------------------------------
  // Signing helpers
  // -------------------------------------------------------------------------

  const ROLE_LABELS = { landlord: 'Landlord', tenant: 'Tenant', guarantor: 'Guarantor' };
  const STATUS_LABELS = {
    sent: 'Awaiting signatures',
    partially_signed: 'Partially signed',
    fully_signed: 'Fully signed',
    signed: 'Fully signed',
    superseded: 'Superseded',
    draft: 'Draft',
  };
  const STATUS_COLOURS = {
    sent: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    partially_signed: 'bg-blue-50 text-blue-800 border-blue-200',
    fully_signed: 'bg-green-50 text-green-800 border-green-200',
    signed: 'bg-green-50 text-green-800 border-green-200',
    superseded: 'bg-gray-50 text-gray-600 border-gray-200',
    draft: 'bg-gray-50 text-gray-600 border-gray-200',
  };

  const loadSigningStatus = async (id) => {
    if (!id) return;
    setSigningLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/written-statements/${id}/signing-status`,
        { withCredentials: true }
      );
      setSigningVersion(data.version || null);
    } catch {
      // Non-fatal
    } finally {
      setSigningLoading(false);
    }
  };

  const loadStatementVersions = async (id) => {
    if (!id) {
      setStatementVersions([]);
      return [];
    }
    setVersionsLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/written-statements/${id}/versions`,
        { withCredentials: true }
      );
      const versions = Array.isArray(data?.versions) ? data.versions : [];
      setStatementVersions(versions);
      return versions;
    } catch {
      setStatementVersions([]);
      return [];
    } finally {
      setVersionsLoading(false);
    }
  };

  const buildDefaultParticipants = () => {
    const parts = [];
    // Landlord
    parts.push({
      role: 'landlord',
      signer_name: landlordNames.filter(Boolean).join(', ') || user?.name || '',
      email: landlordEmail || user?.email || '',
      entity_type: user?.ownership_type === 'company' ? 'company' : 'individual',
      entity_name: user?.ownership_type === 'company' ? (user?.company_name || '') : null,
      signer_role: user?.ownership_type === 'company' ? (user?.director_name ? 'Director' : 'Authorised Signatory') : null,
      required: true,
    });
    // Tenants
    tenants.filter((t) => t.name && t.email).forEach((t) => {
      parts.push({
        role: 'tenant',
        user_id: t.user_id,
        signer_name: t.name,
        email: t.email,
        entity_type: 'individual',
        entity_name: null,
        signer_role: null,
        required: true,
      });
    });
    // Guarantors
    guarantors.filter((g) => g.name && g.email).forEach((g) => {
      parts.push({
        role: 'guarantor',
        signer_name: g.name,
        email: g.email,
        entity_type: 'individual',
        entity_name: null,
        signer_role: null,
        required: true,
      });
    });
    return parts;
  };

  const handleSendForSigning = async () => {
    // Gate on canActOnStatement (draft OR generated), NOT isEditableStatement.
    // After generation the form is locked but sending for signing is still available.
    if (!canActOnStatement) {
      toast.error('Create an amendment before sending this statement again.');
      return;
    }
    const parts = buildDefaultParticipants();
    if (parts.length === 0) { toast.error('No valid participants to send to'); return; }
    setSendingForSigning(true);
    try {
      await axios.post(
        `${API_URL}/api/written-statements/${statementId}/send`,
        { participants: parts },
        { withCredentials: true }
      );
      toast.success('Signing invites sent successfully');
      setStatementStatus('sent');
      await loadSigningStatus(statementId);
      await loadStatementVersions(statementId);
      markStatementChanged();
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to send for signing'));
    } finally {
      setSendingForSigning(false);
    }
  };

  const handleAmend = async () => {
    // If already a draft (e.g. viewing with ?view=1 but PDF file check failed on server),
    // just navigate to the editable form rather than calling the amend API.
    if (statementStatus === 'draft') {
      const params = new URLSearchParams(window.location.search);
      params.delete('view');
      navigate(`/tenancy-agreement?${params.toString()}`);
      return;
    }
    // Clear pdf state immediately so no stale save response can restore it
    setAmending(true);
    setPdfGenerated(false);
    setPdfPath(null);
    setTitleType(null);
    setSigningVersion(null);
    lastGeneratedSnapshotRef.current = null;
    try {
      await axios.post(
        `${API_URL}/api/written-statements/${statementId}/amend`,
        {},
        { withCredentials: true }
      );
      toast.success('A pre-filled draft has been created — make your changes and generate a new PDF.');
      setStatementStatus('draft');
      await loadStatementVersions(statementId);
      markStatementChanged();
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to create amendment'));
    } finally {
      setAmending(false);
    }
  };

  const handleResendSigning = async () => {
    if (resending) return;
    setResending(true);
    try {
      await axios.post(
        `${API_URL}/api/written-statements/${statementId}/resend-signing`,
        {},
        { withCredentials: true }
      );
      toast.success('Signing links reissued \u2014 new emails sent to all outstanding signers');
      await loadSigningStatus(statementId);
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to reissue signing links'));
    } finally {
      setResending(false);
    }
  };

  // -------------------------------------------------------------------------
  // Generate PDF
  // -------------------------------------------------------------------------

  const handleGeneratePdf = async () => {
    if (!isEditableStatement) {
      toast.error('This statement is read-only. Create an amendment before regenerating the PDF.');
      return;
    }
    // Block regeneration when nothing has changed since the last generated PDF.
    if (statementVersions.length > 0) {
      const currentSnapshot = JSON.stringify(buildRequestBody());
      if (currentSnapshot === lastGeneratedSnapshotRef.current) {
        toast.error('No changes detected — the form is identical to the last generated PDF.');
        return;
      }
    }
    setGenerating(true);
    try {
      // Save first
      const id = await handleSaveDraft();
      if (!id) return;

      const { data } = await axios.post(
        `${API_URL}/api/written-statements/${id}/generate-pdf`,
        {},
        { withCredentials: true }
      );
      setPdfGenerated(true);
      setPdfPath(data.pdf_path);
      setTitleType(data.title_type);
      setStatementStatus('generated');
      lastGeneratedSnapshotRef.current = JSON.stringify(buildRequestBody());
      toast.success('Document generated successfully');
      await loadSigningStatus(id);
      await loadStatementVersions(id);
      markStatementChanged();
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to generate PDF'));
    } finally {
      setGenerating(false);
    }
  };

  // -------------------------------------------------------------------------
  // Draft preview (in-memory, no save)
  // -------------------------------------------------------------------------

  const closeDraftPreview = () => {
    draftPreviewRequestRef.current += 1;
    draftPreviewAbortRef.current?.abort();
    draftPreviewAbortRef.current = null;
    setDraftPreviewLoading(false);
    setDraftPreview(null);
  };

  const handleViewDraft = async () => {
    if (!statementId) {
      toast.error('Save the draft before previewing');
      return;
    }
    draftPreviewRequestRef.current += 1;
    const requestId = draftPreviewRequestRef.current;
    draftPreviewAbortRef.current?.abort();
    const controller = new AbortController();
    draftPreviewAbortRef.current = controller;
    setDraftPreview({ pdfData: null, sourceKey: `draft-${statementId}-${Date.now()}`, errorMessage: '' });
    setDraftPreviewLoading(true);
    setViewingDraft(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/written-statements/${statementId}/draft-preview`,
        { responseType: 'arraybuffer', withCredentials: true, signal: controller.signal }
      );
      if (requestId !== draftPreviewRequestRef.current) return;
      const bytes = new Uint8Array(response.data);
      setDraftPreview((prev) => prev ? { ...prev, pdfData: bytes } : prev);
    } catch (err) {
      if (requestId !== draftPreviewRequestRef.current) return;
      const message = err.response?.data?.detail || err.message || 'Could not generate draft preview';
      setDraftPreview((prev) => prev ? { ...prev, errorMessage: message } : prev);
      console.error('[written-statement] Draft preview error:', err);
    } finally {
      if (requestId === draftPreviewRequestRef.current) {
        setDraftPreviewLoading(false);
        setViewingDraft(false);
      }
    }
  };

  // -------------------------------------------------------------------------
  // Download
  // -------------------------------------------------------------------------

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/written-statements/${statementId}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `written_statement_${statementId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to download PDF'));
    }
  };

  const openVersionPdf = async (versionId, versionNumber) => {
    setVersionPreview({ pdfData: null, sourceKey: `version-${versionId}`, errorMessage: '', versionNumber, loading: true });
    try {
      const response = await axios.get(
        `${API_URL}/api/written-statements/${statementId}/versions/${versionId}/download`,
        { responseType: 'arraybuffer', withCredentials: true }
      );
      const bytes = new Uint8Array(response.data);
      setVersionPreview((prev) => prev ? { ...prev, pdfData: bytes, loading: false } : prev);
    } catch (err) {
      const message = fmtApiError(err, 'Failed to open version PDF');
      setVersionPreview((prev) => prev ? { ...prev, errorMessage: message, loading: false } : prev);
    }
  };

  const downloadVersionPdf = async (version) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/written-statements/${statementId}/versions/${version.id}/download`,
        { responseType: 'blob', withCredentials: true }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `written_statement_v${version.version_number || 'history'}_${statementId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(fmtApiError(err, 'Failed to download version PDF'));
    }
  };

  // -------------------------------------------------------------------------
  // Step validation
  // -------------------------------------------------------------------------

  const validateStep1 = () => {
    if (!selectedPropertyId) return 'Please select a property';
    if (!selectedTenancyId) return 'Please select a tenancy';
    if (!landlordNames.some((n) => n.trim())) return 'Enter at least one landlord name';
    for (const [index, name] of landlordNames.filter((n) => n.trim()).entries()) {
      const result = validatePersonFullName(name, `Landlord ${index + 1} Full Name`);
      if (!result.valid) return result.error;
    }
    if (!landlordAddress.line1.trim()) return 'Landlord notice address line 1 is required';
    if (!landlordAddress.city.trim()) return 'Landlord notice address city is required';
    if (!landlordAddress.postcode.trim()) return 'Landlord notice address postcode is required';
    if (!landlordEmail.trim()) return 'Landlord email is required';
    if (!tenants.some((t) => t.name.trim() && t.email.trim())) return 'Enter at least one tenant name and email';
    for (const [index, tenant] of tenants.filter((t) => t.name.trim()).entries()) {
      const result = validatePersonFullName(tenant.name, `Tenant ${index + 1} Full Name`);
      if (!result.valid) return result.error;
    }
    if (!tenancyStartDate) return 'Tenancy start date is required';
    return null;
  };

  const validateStep2 = () => {
    if (!rentAmount || parseFloat(rentAmount) <= 0) return 'Rent amount must be greater than 0';
    const day = parseInt(rentDueDay);
    if (!day || day < 1 || (rentPeriod === 'monthly' && day > 31) || (rentPeriod === 'weekly' && day > 7))
      return `Due day must be between 1 and ${rentPeriod === 'monthly' ? 31 : 7}`;
    if (!paymentMethodType.trim()) return 'Payment method is required';
    if (paymentMethodType === 'bank_transfer') {
      if (!bankDetails.sortCode.trim()) return 'Sort code is required for bank transfer';
      if (!bankDetails.accountNumber.trim()) return 'Account number is required for bank transfer';
    }
    if (depositTaken && !depositScheme) return 'Select a deposit protection scheme';
    return null;
  };

  const handleNext = async () => {
    if (!isViewOnly) {
      let err = null;
      if (currentStep === 1) err = validateStep1();
      if (currentStep === 2) err = validateStep2();
      if (err) { toast.error(err); return; }
      // Pre-fill clause 1.2 max_occupants when entering Step 3
      if (currentStep === 2 && maxOccupants && selectedClauses.includes('1.2')) {
        setClauseOverrides((prev) => ({
          ...prev,
          '1.2': { ...(prev['1.2'] || {}), max_occupants: maxOccupants },
        }));
      }
      await saveDraftQuietly();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep((s) => Math.min(s + 1, 5));
  };

  const handleBack = async () => {
    if (!isViewOnly) await saveDraftQuietly();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentStep((s) => Math.max(s - 1, 1));
  };

  // -------------------------------------------------------------------------
  // Clause toggle helpers
  // -------------------------------------------------------------------------

  const handleClauseToggle = (id) => {
    if (autoIncluded.includes(id)) return;
    setSelectedClauses((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleOverrideChange = (clauseId, fieldKey, value) => {
    setClauseOverrides((prev) => ({
      ...prev,
      [clauseId]: { ...(prev[clauseId] || {}), [fieldKey]: value },
    }));
  };

  const handleSelectAllRecommended = () => {
    const recommended = clauseLibrary
      .filter((c) => c.default_on || c.auto_included)
      .map((c) => c.id);
    setSelectedClauses((prev) => [...new Set([...prev, ...recommended])]);
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const hasOptionalTerms =
    selectedClauses.filter((id) => !autoIncluded.includes(id)).length > 0 ||
    customClauses.some((c) => c.title?.trim() && c.body?.trim());

  const predictedTitleType = hasOptionalTerms ? 'tenancy_agreement' : 'written_statement';

  // -------------------------------------------------------------------------
  // Step renders
  // -------------------------------------------------------------------------

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[#1e3a5f] mb-1">Step 1 of 5 — Parties & Tenancy</h2>
        <p className="text-sm text-muted-foreground">Select a property and tenancy, then confirm the parties' details.</p>
      </div>

      {/* Property selector */}
      <div>
        <Label>Property *</Label>
        <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select a property…" />
          </SelectTrigger>
          <SelectContent>
            {properties.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.title || p.address} — {p.postcode}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Tenancy *</Label>
        <Select
          value={selectedTenancyId}
          onValueChange={setSelectedTenancyId}
          disabled={!selectedPropertyId || tenanciesLoading || tenancies.length === 0}
        >
          <SelectTrigger className="mt-1">
            <SelectValue
              placeholder={
                !selectedPropertyId
                  ? 'Select a property first…'
                  : tenanciesLoading
                    ? 'Loading tenancies…'
                    : tenancies.length === 0
                      ? 'No active tenancies found'
                      : 'Select a tenancy…'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {tenancies.map((tenancy) => {
              const startLabel = tenancy.start_date
                ? new Date(`${tenancy.start_date}T00:00:00`).toLocaleDateString('en-GB')
                : 'Start not set';
              return (
                <SelectItem key={tenancy.id} value={tenancy.id}>
                  {[(getTenancyTenantNames(tenancy).join(', ') || 'Tenant'), startLabel].join(' — ')}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {selectedTenancy && (
          <p className="text-xs text-muted-foreground mt-2">
            {getTenancyTenantNames(selectedTenancy).join(', ') || 'Tenant'}
            {getTenancyTenantEmails(selectedTenancy).length ? ` · ${getTenancyTenantEmails(selectedTenancy).join(', ')}` : ''}
            {selectedTenancy.start_date ? ` · Start ${new Date(`${selectedTenancy.start_date}T00:00:00`).toLocaleDateString('en-GB')}` : ''}
          </p>
        )}
      </div>

      {/* Tenancy type */}
      <div>
        <Label className="mb-2 block">Tenancy type *</Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setTenancyType('whole_property')}
            className={`flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors ${tenancyType === 'whole_property' ? 'border-[#1e3a5f] bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
          >
            <div className="flex w-full items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium text-sm text-gray-800">
                <Building2 className="h-4 w-4 text-[#1e3a5f]" />
                Whole property
              </span>
              {tenancyType === 'whole_property' && <Check className="h-4 w-4 text-[#1e3a5f]" />}
            </div>
            <p className="text-xs text-muted-foreground">The tenant occupies the entire property exclusively.</p>
          </button>
          <button
            type="button"
            onClick={() => setTenancyType('room_in_shared_house')}
            className={`flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors ${tenancyType === 'room_in_shared_house' ? 'border-[#1e3a5f] bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
          >
            <div className="flex w-full items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium text-sm text-gray-800">
                <Users className="h-4 w-4 text-[#1e3a5f]" />
                Room in shared house
              </span>
              {tenancyType === 'room_in_shared_house' && <Check className="h-4 w-4 text-[#1e3a5f]" />}
            </div>
            <p className="text-xs text-muted-foreground">The tenant has exclusive use of a room with shared access to common areas.</p>
          </button>
        </div>
      </div>

      {/* Room-specific fields */}
      {tenancyType === 'room_in_shared_house' && (
        <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-2 text-sm text-blue-800">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              These details will appear in the agreement and identify the specific room being let. Clause language will be automatically adjusted for a shared-house tenancy.
            </span>
          </div>
          <div>
            <Label>Room name / identifier (optional)</Label>
            <Input
              className="mt-1 bg-white"
              value={designatedRoomName}
              onChange={(e) => setDesignatedRoomName(e.target.value)}
              placeholder="e.g. Bedroom 2, Front double room"
            />
          </div>
          <div>
            <Label>Room description (optional)</Label>
            <Textarea
              className="mt-1 bg-white resize-none"
              rows={2}
              value={designatedRoomDescription}
              onChange={(e) => setDesignatedRoomDescription(e.target.value)}
              placeholder="e.g. Second-floor double room with en-suite shower room"
            />
          </div>
        </div>
      )}

      {/* Student let toggle */}
      <div className="flex items-center gap-3">
        <Switch checked={isStudentLet} onCheckedChange={setIsStudentLet} id="student-let" className="hidden md:flex" />
        <button
          type="button"
          onClick={() => setIsStudentLet(!isStudentLet)}
          className="md:hidden flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <Label className="flex items-center gap-1.5 cursor-pointer font-medium text-sm text-gray-700">
            <GraduationCap className="h-4 w-4 text-[#1e3a5f]" />
            Student let
          </Label>
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isStudentLet ? 'bg-[#1e3a5f] border-[#1e3a5f]' : 'border-gray-300'}`}>
            {isStudentLet && <Check className="h-4 w-4 text-white" />}
          </div>
        </button>
        <Label htmlFor="student-let" className="hidden md:flex items-center gap-1.5 cursor-pointer">
          <GraduationCap className="h-4 w-4 text-[#1e3a5f]" />
          Student let
        </Label>
      </div>
      {isStudentLet && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2 text-sm text-blue-800">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Ground 4A prior notice will be included, allowing you to seek possession at the end of the academic year.</span>
        </div>
      )}

      {/* Landlord details */}
      <div className="space-y-4">
        <h3 className="font-medium text-sm text-gray-700">Landlord details</h3>
        {landlordNames.map((name, i) => (
          <div key={i} className="flex gap-2">
            <div className="flex-1">
              <Label>Landlord Full Name {landlordNames.length > 1 ? i + 1 : ''} *</Label>
              <Input
                className="mt-1"
                value={name}
                onChange={(e) => {
                  const n = [...landlordNames];
                  n[i] = e.target.value;
                  setLandlordNames(n);
                }}
                placeholder="Full Name (e.g. John Smith)"
              />
            </div>
            {landlordNames.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="mt-6 text-destructive"
                onClick={() => setLandlordNames((prev) => prev.filter((_, j) => j !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setLandlordNames((prev) => [...prev, ''])}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add joint landlord
        </Button>

        <div>
          <Label>Landlord email *</Label>
          <Input
            className="mt-1"
            type="email"
            value={landlordEmail}
            onChange={(e) => setLandlordEmail(e.target.value)}
          />
        </div>

        <div>
          <Label className="mb-1 block">Landlord notice address *</Label>
          <AddressInput
            defaultValue={fmtAddressForInput(landlordAddress)}
            placeholder="Start typing a UK address or postcode…"
            onAddressSelect={(addr) => {
              if (addr.line1 || addr.town || addr.postcode) {
                // Structured result from Google Places — preserve every field including country.
                setLandlordAddress({
                  line1: addr.line1 || '',
                  line2: addr.line2 || '',
                  city: addr.town || addr.city || '',
                  postcode: addr.postcode || '',
                  country: addr.country || 'United Kingdom',
                });
              } else if (addr.formatted) {
                // Fallback plain string (Places API unavailable or blur-only) — parse it.
                setLandlordAddress(parseAddressString(addr.formatted));
              }
            }}
          />
        </div>
      </div>

      {/* Tenant details */}
      <div className="space-y-3">
        <h3 className="font-medium text-sm text-gray-700">Tenant details</h3>
        {tenants.map((t, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <Label>Tenant {tenants.length > 1 ? i + 1 : ''} Full Name *</Label>
              <Input
                className="mt-1"
                value={t.name}
                onChange={(e) => {
                  const arr = [...tenants];
                  arr[i] = { ...arr[i], name: e.target.value };
                  setTenants(arr);
                }}
                placeholder="Full Name (e.g. John Smith)"
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>Tenant {tenants.length > 1 ? i + 1 : ''} email *</Label>
                <Input
                  className="mt-1"
                  type="email"
                  value={t.email}
                  onChange={(e) => {
                    const arr = [...tenants];
                    arr[i] = { ...arr[i], email: e.target.value };
                    setTenants(arr);
                  }}
                />
              </div>
              {tenants.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-6 text-destructive"
                  onClick={() => setTenants((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setTenants((prev) => [...prev, { name: '', email: '' }])}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add joint tenant
        </Button>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Tenancy start date *</Label>
          <Input
            className="mt-1"
            type="date"
            value={tenancyStartDate}
            onChange={(e) => setTenancyStartDate(e.target.value)}
          />
        </div>
        <div>
          <Label>Maximum occupants (optional)</Label>
          <Input
            className="mt-1"
            type="number"
            min="1"
            value={maxOccupants}
            onChange={(e) => setMaxOccupants(e.target.value)}
            placeholder="e.g. 4"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[#1e3a5f] mb-1">Step 2 of 5 — Rent & Deposit</h2>
        <p className="text-sm text-muted-foreground">Set out the financial arrangements for the tenancy.</p>
      </div>

      {/* Rent */}
      <div className="space-y-4">
        <h3 className="font-medium text-sm text-gray-700">Rent</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Rent amount *</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                className="pl-7"
                value={rentAmount}
                onChange={(e) => setRentAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <Label>Period *</Label>
            <Select value={rentPeriod} onValueChange={setRentPeriod}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Due on day *</Label>
            <Input
              className="mt-1"
              type="number"
              min="1"
              max={rentPeriod === 'monthly' ? 31 : 7}
              value={rentDueDay}
              onChange={(e) => setRentDueDay(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Day 1–{rentPeriod === 'monthly' ? '31' : '7'} of each {rentPeriod}
            </p>
          </div>
          <div>
            <Label>Payment method *</Label>
            <Select value={paymentMethodType} onValueChange={setPaymentMethodType}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="standing_order">Standing Order</SelectItem>
                <SelectItem value="direct_debit">Direct Debit</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {paymentMethodType === 'bank_transfer' && (
          <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
            <div>
              <Label>Sort Code *</Label>
              <Input
                className="mt-1"
                value={bankDetails.sortCode}
                onChange={(e) => setBankDetails(prev => ({ ...prev, sortCode: e.target.value }))}
                placeholder="XX-XX-XX"
                maxLength={8}
              />
            </div>
            <div>
              <Label>Account Number *</Label>
              <Input
                className="mt-1"
                value={bankDetails.accountNumber}
                onChange={(e) => setBankDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
                placeholder="XXXXXXXX"
                maxLength={8}
              />
            </div>
            <div>
              <Label>Account Name</Label>
              <Input
                className="mt-1"
                value={bankDetails.accountName}
                onChange={(e) => setBankDetails(prev => ({ ...prev, accountName: e.target.value }))}
                placeholder="Account holder name"
              />
            </div>
            <div>
              <Label>Account Type</Label>
              <Select value={bankDetails.accountType} onValueChange={(value) => setBankDetails(prev => ({ ...prev, accountType: value }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {paymentMethodType === 'other' && (
          <div className="mt-4">
            <Label>Payment Details</Label>
            <Textarea
              className="mt-1"
              value={bankDetails.accountName} // reusing accountName for other details
              onChange={(e) => setBankDetails(prev => ({ ...prev, accountName: e.target.value }))}
              placeholder="Describe the payment method"
            />
          </div>
        )}
        <div>
          <Label>Initial payment at signing (optional)</Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              className="pl-7"
              value={initialRentPayment}
              onChange={(e) => setInitialRentPayment(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            First period's rent payable at signing (separate from the ongoing rent). Leave blank if not applicable.
          </p>
        </div>
      </div>

      {/* Deposit */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch checked={depositTaken} onCheckedChange={setDepositTaken} id="deposit-taken" className="hidden md:flex" />
          <button
            type="button"
            onClick={() => setDepositTaken(!depositTaken)}
            className="md:hidden flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Label className="cursor-pointer font-medium text-sm text-gray-700">
              A deposit is being taken
            </Label>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${depositTaken ? 'bg-[#1e3a5f] border-[#1e3a5f]' : 'border-gray-300'}`}>
              {depositTaken && <Check className="h-4 w-4 text-white" />}
            </div>
          </button>
          <Label htmlFor="deposit-taken" className="hidden md:flex cursor-pointer font-medium text-sm text-gray-700">
            A deposit is being taken
          </Label>
        </div>
        {depositTaken && (
          <div className="space-y-3 pl-4 border-l-2 border-gray-200">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Deposit amount *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="pl-7"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Protection scheme *</Label>
                <Select value={depositScheme} onValueChange={setDepositScheme}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select scheme…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DPS">DPS</SelectItem>
                    <SelectItem value="mydeposits">mydeposits</SelectItem>
                    <SelectItem value="TDS">TDS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Held by (optional)</Label>
              <Input
                className="mt-1"
                value={depositHeldBy}
                onChange={(e) => setDepositHeldBy(e.target.value)}
                placeholder="e.g. Landlord / Agent name"
              />
            </div>
          </div>
        )}
      </div>

      {/* Utilities */}
      <div className="space-y-3">
        <h3 className="font-medium text-sm text-gray-700">Utilities & charges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {UTILITY_KEYS.map((key) => (
            <div key={key}>
              <Label className="text-xs">{UTILITY_LABELS[key]}</Label>
              <Select
                value={utilities[key]}
                onValueChange={(v) => setUtilities((u) => ({ ...u, [key]: v }))}
              >
                <SelectTrigger className="mt-1 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UTILITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[#1e3a5f] mb-1">Step 3 of 5 — Additional Terms (Standard Clauses)</h2>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2 text-sm text-blue-800">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          The Written Statement of Terms above covers your legal requirement. You can optionally include
          additional terms to set out rights and obligations. By adding terms, this document will serve
          as your full Assured Periodic Tenancy (APT) agreement.
        </span>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex gap-2 text-sm text-gray-600">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
        <span>
          Leasehold and HMO-specific clauses are available under <strong>General</strong> below.
          Select them if applicable to your property.
        </span>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {selectedClauses.length} clause{selectedClauses.length !== 1 ? 's' : ''} selected
        </p>
        <Button type="button" variant="outline" size="sm" onClick={handleSelectAllRecommended}>
          <Check className="h-3.5 w-3.5 mr-1" />
          Select all recommended
        </Button>
      </div>

      {clauseLibrary.length > 0 ? (
        <ClauseLibrary
          clauses={clauseLibrary}
          selectedClauses={selectedClauses}
          autoIncluded={autoIncluded}
          clauseOverrides={clauseOverrides}
          onToggle={handleClauseToggle}
          onOverrideChange={handleOverrideChange}
        />
      ) : (
        <p className="text-sm text-muted-foreground">Select a tenancy to see available clauses.</p>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[#1e3a5f] mb-1">Step 4 of 5 — Additional Terms (Guarantors & Clauses)</h2>
        <p className="text-sm text-muted-foreground">Add bespoke terms and any guarantors for this tenancy. Guarantors entered here will be included in the generated PDF and automatically added as signing participants.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 text-sm text-amber-800">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Clauses you write here have not been reviewed or endorsed by SIYAK. You are solely responsible
          for their content. SIYAK recommends independent legal advice before adding custom clauses.
        </span>
      </div>

      {/* Custom clauses */}
      <div className="space-y-4">
        <h3 className="font-medium text-sm text-gray-700">Custom clauses</h3>
        {customClauses.map((cc, i) => (
          <Card key={i}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Custom clause {i + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive h-7 w-7"
                  onClick={() => setCustomClauses((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  className="mt-1"
                  value={cc.title}
                  onChange={(e) => {
                    const arr = [...customClauses];
                    arr[i] = { ...arr[i], title: e.target.value };
                    setCustomClauses(arr);
                  }}
                  placeholder="e.g. Bicycle storage"
                />
              </div>
              <div>
                <Label>Clause text</Label>
                <Textarea
                  className="mt-1 min-h-[80px]"
                  value={cc.body}
                  onChange={(e) => {
                    const arr = [...customClauses];
                    arr[i] = { ...arr[i], body: e.target.value };
                    setCustomClauses(arr);
                  }}
                  placeholder="Write the full clause text here…"
                />
              </div>
            </CardContent>
          </Card>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={() => setCustomClauses((prev) => [...prev, { title: '', body: '' }])}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add a custom clause
        </Button>
      </div>

      {/* Guarantors */}
      <div className="space-y-3">
        <h3 className="font-medium text-sm text-gray-700">Guarantors</h3>
        <p className="text-xs text-muted-foreground">Enter guarantor details before generating. They will appear on the signature page and receive a signing link when the document is sent.</p>
        {guarantors.map((g, i) => (
          <div key={i} className="space-y-2 p-3 border border-gray-200 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input
                placeholder="Full Name (e.g. John Smith)"
                value={g.name}
                onChange={(e) => setGuarantors((prev) => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                maxLength={120}
              />
              <div className="flex gap-2">
                <Input
                  placeholder="Guarantor email"
                  type="email"
                  value={g.email}
                  onChange={(e) => setGuarantors((prev) => prev.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                  maxLength={200}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setGuarantors((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </div>
            <AddressInput
              defaultValue={g.address || ''}
              placeholder="Address for service of notices (England/Wales)"
              helperText="Guarantor's address in England or Wales for valid service of notices (required by clause 15.6)."
              onAddressSelect={(addr) => setGuarantors((prev) => prev.map((x, j) => j === i ? { ...x, address: addr.formatted || '' } : x))}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setGuarantors((prev) => [...prev, { name: '', email: '', address: '' }])}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add a guarantor
        </Button>
      </div>
    </div>
  );

  const renderVersionHistory = () => {
    if (!statementId) return null;

    const hasMultipleVersions = statementVersions.length > 0;
    const currentVersion = statementStatus === 'draft'
      ? null
      : (statementVersions.find((version) => version.status !== 'superseded') || statementVersions[0] || null);
    const previousVersions = statementStatus === 'draft'
      ? statementVersions
      : statementVersions.filter((version) => version.id !== currentVersion?.id);
    const currentDate = currentVersion?.completed_at || currentVersion?.sent_at || currentVersion?.created_at;

    return (
      <Card data-testid="statement-version-history" className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#1e3a5f] flex items-center gap-2">
            <History className="h-4 w-4" />
            Statement versions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div
            data-testid="current-statement-version"
            className={`rounded-lg border border-[#1e3a5f]/20 bg-blue-50/60 p-3 ${
              pdfGenerated ? 'cursor-pointer hover:bg-blue-100/60 transition-colors' : ''
            }`}
            onClick={() => {
              if (pdfGenerated) handleDownload();
            }}
            title={pdfGenerated ? 'Download current statement' : undefined}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-[#1e3a5f]">
                  Current statement
                  {currentVersion?.version_number ? ` — Version ${currentVersion.version_number}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  {currentVersion
                    ? `Created ${fmtStatementDate(currentDate)}`
                    : pdfGenerated
                      ? 'PDF generated — click to download'
                      : hasMultipleVersions
                        ? 'Draft amendment in progress'
                        : null}
                </p>
              </div>
              {statementId && statementStatus === 'draft' && !pdfGenerated ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleViewDraft(); }}
                  disabled={viewingDraft}
                  title="Click to preview draft"
                >
                  <Badge variant="outline" className="capitalize cursor-pointer hover:bg-muted transition-colors">
                    {viewingDraft ? 'Loading…' : 'draft'}
                  </Badge>
                </button>
              ) : statementStatus === 'draft' && pdfGenerated ? (
                <Badge className="capitalize bg-green-100 text-green-800 border-green-300">
                  generated
                </Badge>
              ) : (
                <Badge variant="outline" className="capitalize">
                  {statementStatus.replace('_', ' ')}
                </Badge>
              )}
            </div>
          </div>

          {versionsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading version history…
            </div>
          ) : previousVersions.length > 0 ? (
            <details className="rounded-lg border p-3" data-testid="previous-statement-versions">
              <summary className="cursor-pointer text-sm font-medium text-[#1e3a5f]">
                Previous versions ({previousVersions.length})
              </summary>
              <div className="mt-3 space-y-2">
                {previousVersions.map((version) => {
                  const versionDate = version.completed_at || version.sent_at || version.created_at;
                  const hasPdf = Boolean(version.pdf_available);
                  return (
                    <div key={version.id} className="rounded-md border bg-white p-3 text-sm" data-testid="statement-version-row">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">Version {version.version_number || '—'}</span>
                            <Badge variant="outline" className="capitalize">
                              {(version.status || 'unknown').replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Created {fmtStatementDate(version.created_at)}
                            {version.sent_at ? ` · Sent ${fmtStatementDate(version.sent_at)}` : ''}
                            {version.completed_at ? ` · Signed ${fmtStatementDate(version.completed_at)}` : ''}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!hasPdf}
                            onClick={() => openVersionPdf(version.id, version.version_number)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                            View
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!hasPdf}
                            onClick={() => downloadVersionPdf(version)}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          ) : (
            <p className="text-sm text-muted-foreground">No previous versions yet.</p>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderStep5 = () => {
    const validCustom = customClauses.filter((c) => c.title?.trim() && c.body?.trim());
    const nonAutoSelected = selectedClauses.filter((id) => !autoIncluded.includes(id));

    // Group selected clauses by category for summary
    const clauseSummary = {};
    nonAutoSelected.forEach((id) => {
      const cl = clauseLibrary.find((c) => c.id === id);
      if (cl) {
        clauseSummary[cl.category_label] = (clauseSummary[cl.category_label] || 0) + 1;
      }
    });

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-[#1e3a5f] mb-1">Step 5 of 5 — Review & Generate</h2>
          <p className="text-sm text-muted-foreground">Review the summary below, then save or generate your document.</p>
        </div>

        {/* Document title preview */}
        <div className={`rounded-lg border p-3 text-sm ${
          (titleType || predictedTitleType) === 'tenancy_agreement'
            ? 'bg-blue-50 border-blue-200 text-blue-800'
            : 'bg-gray-50 border-gray-200 text-gray-700'
        }`}>
          <span className="font-semibold">Document title: </span>
          {(titleType || predictedTitleType) === 'tenancy_agreement'
            ? 'Assured Periodic Tenancy Agreement'
            : 'Written Statement of Terms'}
        </div>

        {/* Part 1 summary */}
        <div>
          <h3 className="font-medium text-sm text-[#1e3a5f] mb-2">Part 1 — Written Statement of Terms</h3>
          <div className="space-y-1 text-sm text-gray-700">
            <p><span className="text-muted-foreground">Landlord:</span> {landlordNames.filter(Boolean).join(', ') || '—'}</p>
            <p><span className="text-muted-foreground">Tenant(s):</span> {tenants.filter((t) => t.name).map((t) => t.name).join(', ') || '—'}</p>
            <p><span className="text-muted-foreground">Property:</span> {selectedProperty ? formatStructuredAddress(parsePropertyAddress(selectedProperty), formatPropertyAddress(selectedProperty.address, selectedProperty.postcode)) : '—'}</p>
            <p><span className="text-muted-foreground">Tenancy:</span> {getTenancyTenantNames(selectedTenancy).join(', ') || '—'}</p>
            <p><span className="text-muted-foreground">Tenancy start:</span> {tenancyStartDate ? new Date(tenancyStartDate + 'T00:00:00').toLocaleDateString('en-GB') : '—'}</p>
            <p><span className="text-muted-foreground">Rent:</span> {rentAmount ? `${fmtCurrency(rentAmount)} per ${rentPeriod}` : '—'}</p>
            <p><span className="text-muted-foreground">Deposit:</span> {depositTaken && depositAmount ? `${fmtCurrency(depositAmount)} — ${depositScheme || 'scheme not selected'}` : 'No deposit'}</p>
            {isStudentLet && (
              <p className="text-blue-700"><span className="text-muted-foreground">Ground 4A prior notice:</span> included</p>
            )}
            <p className="text-muted-foreground text-xs mt-1">Statutory information will be included automatically in Section E.</p>
          </div>
        </div>

        {/* Part 2 summary */}
        {nonAutoSelected.length > 0 ? (
          <div>
            <h3 className="font-medium text-sm text-[#1e3a5f] mb-2">
              Part 2 — Additional Terms ({nonAutoSelected.length} clause{nonAutoSelected.length !== 1 ? 's' : ''})
            </h3>
            <div className="space-y-1">
              {Object.entries(clauseSummary).map(([cat, count]) => (
                <div key={cat} className="text-sm text-gray-700 flex justify-between">
                  <span>{cat}</span>
                  <span className="text-muted-foreground">{count} clause{count !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        ) : !validCustom.length ? (
          <p className="text-sm text-muted-foreground italic">
            No additional terms selected. The mandatory Written Statement of Terms only will be generated.
          </p>
        ) : null}

        {/* Custom clauses summary */}
        {validCustom.length > 0 && (
          <div>
            <h3 className="font-medium text-sm text-[#1e3a5f] mb-2">
              Custom Clauses ({validCustom.length})
            </h3>
            <ul className="space-y-0.5">
              {validCustom.map((cc, i) => (
                <li key={i} className="text-sm text-gray-700">Q.{i + 1} — {cc.title}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Action buttons or success state */}
        {(pdfGenerated || ['generated', 'sent', 'partially_signed', 'fully_signed', 'superseded'].includes(statementStatus)) ? (
          <div className="space-y-5">
            {!isEditableStatement && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                {statementStatus === 'draft'
                  ? 'You are viewing a read-only preview of the generated PDF. Use Amend from the dashboard to make changes.'
                  : 'This statement is read-only because it has already moved beyond draft status. Download the current PDF or create an amendment to return it to draft for editing.'}
              </div>
            )}
            {pdfPath ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Document generated successfully.</p>
                  <p className="text-sm text-green-700 mt-0.5">
                    {(titleType || predictedTitleType) === 'tenancy_agreement'
                      ? 'Assured Periodic Tenancy Agreement'
                      : 'Written Statement of Terms'}{' '}
                    is ready to download or send for signing.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>PDF file is unavailable for download, but you can still send for signing or create an amendment.</p>
              </div>
            )}

            {/* Download — only when file is available */}
            {pdfPath && (
              <div className="flex flex-wrap gap-2">
                {statementVersions.length > 0 ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="w-full sm:w-auto">
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[220px]">
                      <DropdownMenuLabel>Choose version</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleDownload}>
                        <Download className="h-3.5 w-3.5 mr-2 shrink-0" />
                        Current PDF
                      </DropdownMenuItem>
                      {statementVersions.map((v) => (
                        <DropdownMenuItem
                          key={v.id}
                          onClick={() => downloadVersionPdf(v)}
                          disabled={!v.pdf_available}
                        >
                          <Download className="h-3.5 w-3.5 mr-2 shrink-0" />
                          Version {v.version_number || '?'}
                          <span className="ml-auto text-xs text-muted-foreground capitalize">
                            {(v.status || '').replace('_', ' ')}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button className="w-full sm:w-auto" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            )}

            {/* ── Signing panel ─────────────────────────────────────────── */}
            <Card className="border-[#1e3a5f]/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-[#1e3a5f] flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Send for Signing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">

                {/* Status banner when a version exists */}
                {signingLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading signing status…
                  </div>
                )}

                {signingVersion && signingVersion.status !== 'generated' && !signingLoading && (
                  <div className={`rounded-lg border px-3 py-2 text-sm font-medium ${STATUS_COLOURS[signingVersion.status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                    {STATUS_LABELS[signingVersion.status] || signingVersion.status}
                    {' — '}Version {signingVersion.version_number}
                  </div>
                )}

                {/* Participant list from latest version */}
                {signingVersion?.participants?.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Signatories</p>
                    {signingVersion.participants.map((p, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-gray-50 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">{p.signer_name}</span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {ROLE_LABELS[p.role] || p.role}
                          </Badge>
                          {p.entity_type === 'company' && (
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        {p.signed
                          ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                          : <div className="h-4 w-4 rounded-full border-2 border-gray-300 shrink-0" />}
                      </div>
                    ))}
                  </div>
                )}

            {/* Guarantor inputs — only when no active version yet */}
                {(!signingVersion || ['superseded', 'fully_signed', 'generated'].includes(signingVersion?.status)) && guarantors.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Guarantors
                    </p>
                    {guarantors.filter((g) => g.name?.trim()).map((g, i) => (
                      <div key={i} className="flex items-center gap-2 py-1.5 px-2.5 rounded-lg bg-gray-50 text-sm">
                        <span className="font-medium text-gray-800">{g.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">Guarantor</Badge>
                        <span className="text-xs text-muted-foreground ml-auto">{g.email}</span>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">Guarantors were set in Step 4 and will be included as signing participants.</p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {/* Send / Resend */}
                  {canActOnStatement && (!signingVersion || ['superseded', 'generated'].includes(signingVersion.status)) && (
                    <Button
                      onClick={handleSendForSigning}
                      disabled={sendingForSigning}
                      className="bg-[#1e3a5f] hover:bg-[#163050]"
                    >
                      {sendingForSigning
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <Send className="h-4 w-4 mr-2" />}
                      Send for signing
                    </Button>
                  )}

                  {/* Refresh status */}
                  {signingVersion && !['superseded', 'generated'].includes(signingVersion.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadSigningStatus(statementId)}
                      disabled={signingLoading}
                    >
                      {signingLoading
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                      Refresh
                    </Button>
                  )}

                  {/* Resend signing links — shown only when awaiting signatures */}
                  {statementId && ['sent', 'partially_signed'].includes(statementStatus) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmResend(true)}
                      disabled={resending || signingLoading}
                    >
                      {resending
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                      Resend signing links
                    </Button>
                  )}

                  {/* Amend */}
                  {statementId && !canActOnStatement && !isEditableStatement && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAmend}
                      disabled={amending}
                    >
                      {amending
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <PenLine className="h-3.5 w-3.5 mr-1.5" />}
                      Create amendment
                    </Button>
                  )}
                </div>

                {canActOnStatement && (!signingVersion || ['superseded', 'generated'].includes(signingVersion.status)) && (
                  <p className="text-xs text-muted-foreground">
                    Invites will be emailed to the landlord, all tenants on this statement, and any guarantors added in Step 4.
                    Each recipient gets a unique 7-day signing link — no portal account required for guarantors.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Resend confirmation dialog */}
            <AlertDialog open={confirmResend} onOpenChange={setConfirmResend}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Resend signing links?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will immediately invalidate all previous signing links and send
                    new ones to every outstanding signer. Anyone who hasn’t signed yet
                    will need to use the new link from their email.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      setConfirmResend(false);
                      handleResendSigning();
                    }}
                  >
                    Resend links
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Compliance checklist */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-[#1e3a5f]">
                  Your document has been generated. Remember to also:
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {COMPLIANCE_ITEMS.map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="mt-0.5 shrink-0">
                      <div className="w-4 h-4 rounded border-2 border-gray-300" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Button
              variant="outline"
              onClick={() => {
                navigate('/landlord?tab=tenants');
              }}
            >
              Back to Tenancy Management
            </Button>
          </div>
        ) : !isEditableStatement ? (
          // View-only draft with no PDF generated yet (edge case)
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start gap-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">PDF not yet generated</p>
                <p className="text-xs mt-0.5 text-amber-700">
                  No PDF has been generated for this statement yet. Open the draft to edit and generate one.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAmend}
              disabled={amending}
            >
              {amending
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <PenLine className="h-3.5 w-3.5 mr-1.5" />}
              Open draft
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 pt-2">
            {statementId && (
              <Button
                type="button"
                variant="outline"
                onClick={handleViewDraft}
                disabled={generating || viewingDraft}
              >
                {viewingDraft
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Eye className="h-4 w-4 mr-2" />}
                View draft
              </Button>
            )}
            <Button
              onClick={handleGeneratePdf}
              disabled={generating || viewingDraft}
              className="bg-[#1e3a5f] hover:bg-[#163050]"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              {statementVersions.length > 0 ? 'Regenerate PDF' : 'Generate PDF'}
            </Button>
          </div>
        )}

        {renderVersionHistory()}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-8 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-[#1e3a5f]">Written Statement</h1>
            {statementId && (
              <Badge variant="outline" className="text-muted-foreground">
                {isEditableStatement
                  ? (statementVersions.length > 0 && !pdfGenerated ? 'Draft amendment in progress' : 'Draft')
                  : `Read-only ${statementStatus.replace(/_/g, ' ')}`}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1">Generate your Written Statement of Terms</p>
        </div>

        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} allowDirectNavigation={Boolean(statementId && (isEditableStatement || isViewOnly))} onStepClick={async (step) => {
          if (!isEditableStatement && !isViewOnly) return;
          if (isEditableStatement) await saveDraftQuietly();
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setCurrentStep(step);
        }} />

        {/* Wizard card */}
        <Card>
          <CardContent className="p-4 sm:p-6 md:p-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}

            {/* Navigation */}
            {currentStep < 5 && (isEditableStatement || isViewOnly) && (
              <div className="pt-6 mt-6 border-t space-y-2">
                {/* Skip row — only shown on steps 3 & 4 in edit mode */}
                {currentStep === 3 && !isViewOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full sm:hidden text-sm"
                    onClick={() => {
                      setSelectedClauses([...autoIncluded]);
                      setClauseOverrides({});
                      setCurrentStep(4);
                    }}
                  >
                    Skip – Written statement only
                  </Button>
                )}
                {currentStep === 4 && !isViewOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full sm:hidden text-sm"
                    onClick={() => {
                      setCustomClauses([]);
                      setCurrentStep(5);
                    }}
                  >
                    Skip — no custom clauses
                  </Button>
                )}
                {/* Back / [Skip desktop] / Next row */}
                <div className="flex items-center justify-between gap-2">
                  {currentStep > 1 ? (
                    <Button type="button" variant="outline" className="min-w-[80px]" onClick={handleBack}>
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back
                    </Button>
                  ) : (
                    <div />
                  )}
                  <div className="flex gap-2 items-center">
                    {currentStep === 3 && !isViewOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="hidden sm:inline-flex text-sm"
                        onClick={() => {
                          setSelectedClauses([...autoIncluded]);
                          setClauseOverrides({});
                          setCurrentStep(4);
                        }}
                      >
                        Skip – Written statement only
                      </Button>
                    )}
                    {currentStep === 4 && !isViewOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="hidden sm:inline-flex text-sm"
                        onClick={() => {
                          setCustomClauses([]);
                          setCurrentStep(5);
                        }}
                      >
                        Skip — no custom clauses
                      </Button>
                    )}
                    <Button type="button" className="min-w-[80px]" onClick={handleNext}>
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Back button on step 5 — only when PDF not yet generated AND statement is
                 a draft. Non-draft statements with no PDF show the amend panel instead. */}
            {currentStep === 5 && !pdfGenerated && isEditableStatement && (
              <div className="pt-6 mt-6 border-t">
                <Button type="button" variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <PdfPreviewDialog
        open={Boolean(versionPreview)}
        onOpenChange={(open) => { if (!open) setVersionPreview(null); }}
        title={
          <span className="flex min-w-0 items-center gap-2">
            <FileText className="h-5 w-5 shrink-0 text-royal-600" />
            <span className="truncate">
              {versionPreview?.versionNumber ? `Version ${versionPreview.versionNumber}` : 'Statement Version'}
            </span>
          </span>
        }
        description="Historical version of this written statement."
        pdfData={versionPreview?.pdfData}
        sourceKey={versionPreview?.sourceKey}
        loading={versionPreview?.loading}
        loadingTitle="Loading version PDF"
        errorMessage={versionPreview?.errorMessage}
        unavailableMessage="Version PDF unavailable."
      />

      <PdfPreviewDialog
        open={Boolean(draftPreview)}
        onOpenChange={(open) => { if (!open) closeDraftPreview(); }}
        title={
          <span className="flex min-w-0 items-center gap-2">
            <FileText className="h-5 w-5 shrink-0 text-royal-600" />
            <span className="truncate">Draft Written Statement Preview</span>
          </span>
        }
        description="In-memory preview — no changes are saved and no document is sent."
        pdfData={draftPreview?.pdfData}
        sourceKey={draftPreview?.sourceKey}
        loading={draftPreviewLoading}
        loadingTitle="Generating draft PDF"
        loadingDescription="This preview is rendered on the server and is not saved or sent."
        errorMessage={draftPreview?.errorMessage}
        unavailableMessage="Draft preview unavailable."
      />
    </Layout>
  );
};
